import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import RootLanding from '../../components/layouts/RootLanding';
import { BASE_URL, getFetcherPublic } from '../../services/api';
import { Paginasi } from '../../components/fragments/landing/publik/Elemen';
import Ikon from '../../components/fragments/landing/publik/Ikon';
import { TOPIK } from '../../components/fragments/landing/publik/topikPublik';

/*
 * Halaman "Dokumen & Peraturan" (frame Figma "Dokumen Kebijakan"). Kategori di
 * sisi kiri adalah TOPIK (Merkuri, PCB, Pelayanan Publik, Konvensi), bukan jenis
 * peraturan - keputusan pengguna 5 Nov 2026. Jumlah dokumen kecil, jadi semuanya
 * diambil sekali lalu disaring, diurutkan, dan dipaginasi di browser; itu juga
 * yang memungkinkan angka per kategori dan saringan tahun tanpa mengubah API.
 */
const PER_HALAMAN = 5;
const URUTAN = [
    { kunci: 'terbaru', label: 'Terbaru' },
    { kunci: 'terlama', label: 'Terlama' },
    { kunci: 'judul', label: 'Judul A-Z' },
];

const punya = (doc, kategori) => doc.categories?.some((c) => c.name.toLowerCase() === kategori.toLowerCase());
const tahun = (doc) => new Date(doc.createdAt).getFullYear();
const formatBerkas = (doc) => (doc.attachmentsUrl?.[0]?.split('.').pop() || 'berkas').toUpperCase();

/* Judul dokumen di DB banyak yang kapital semua; tampilkan sebagai Judul Kalimat. */
function rapikanJudul(judul = '') {
    if (judul !== judul.toUpperCase()) return judul;
    return judul
        .toLowerCase()
        .replace(/(^|\s)(\S)/g, (m, s, h) => s + h.toUpperCase())
        .replace(/\bB3\b/gi, 'B3');
}

function Pilihan({ label, nilai, setNilai, opsi, className = '' }) {
    return (
        <label className={`relative flex h-12 items-center rounded-lg border border-pb-bingkai bg-white px-4 ${className}`}>
            <span className="sr-only">{label}</span>
            <select
                value={nilai}
                onChange={(e) => setNilai(e.target.value)}
                className="w-full cursor-pointer appearance-none bg-transparent pr-6 font-body text-sm text-pb-judul outline-none"
            >
                {opsi.map((o) => (
                    <option key={o.kunci} value={o.kunci}>
                        {o.label}
                    </option>
                ))}
            </select>
            <Ikon nama="bawah" className="pointer-events-none absolute right-3 size-4 text-ink-500" />
        </label>
    );
}

function BarisDokumen({ doc }) {
    const berkas = doc.attachmentsUrl?.[0] ? `${BASE_URL}${doc.attachmentsUrl[0]}` : null;
    const kategori = doc.categories?.find((c) => TOPIK.some((t) => t.kategori === c.name))?.name || doc.categories?.[0]?.name || 'Dokumen';
    return (
        <article className="flex flex-col gap-4 rounded-xl border border-pb-bingkai bg-white p-5 sm:flex-row sm:items-center sm:gap-5">
            <span className="inline-flex size-[72px] shrink-0 items-center justify-center rounded-lg bg-pb-kabut text-pb-aksen">
                <Ikon nama="berkas" className="size-7" />
            </span>
            <div className="min-w-0 flex-1">
                <h3 className="font-display text-base font-semibold text-pb-judul">{rapikanJudul(doc.title)}</h3>
                <p className="mt-2 flex flex-wrap gap-x-3 gap-y-1 font-body text-[13px] text-ink-500">
                    <span>{tahun(doc)}</span>
                    <span aria-hidden="true">·</span>
                    <span>{kategori}</span>
                    <span aria-hidden="true">·</span>
                    <span>{formatBerkas(doc)}</span>
                </p>
            </div>
            {berkas && (
                <div className="flex shrink-0 gap-3">
                    <a
                        href={berkas}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex h-11 items-center rounded-lg border border-g-600 px-4 font-body text-sm font-semibold text-g-600 hover:bg-pb-kabut"
                    >
                        Lihat
                    </a>
                    <a
                        href={berkas}
                        download={doc.title}
                        className="inline-flex h-11 items-center gap-2 rounded-lg bg-o-500 px-4 font-body text-sm font-semibold text-white hover:bg-o-600"
                    >
                        <Ikon nama="unduh" className="size-4" />
                        Unduh
                    </a>
                </div>
            )}
        </article>
    );
}

export default function DocumentPage() {
    const [params, setParams] = useSearchParams();
    const kategori = params.get('kategori');
    const [ketikan, setKetikan] = useState('');
    const [cari, setCari] = useState('');
    const [urutan, setUrutan] = useState('terbaru');
    const [tahunPilih, setTahunPilih] = useState('semua');
    const [halaman, setHalaman] = useState(1);

    const { data, isLoading } = useSWR('/api/content/search-document?page=1&limit=500&sortBy=createdAt', getFetcherPublic);
    const semua = useMemo(() => data?.data ?? [], [data]);

    const daftarTahun = useMemo(() => [...new Set(semua.map(tahun))].sort((a, b) => b - a), [semua]);

    const hasil = useMemo(() => {
        const kunci = cari.toLowerCase();
        const saring = semua.filter(
            (d) =>
                (!kategori || punya(d, kategori)) &&
                (tahunPilih === 'semua' || String(tahun(d)) === tahunPilih) &&
                (!kunci || d.title.toLowerCase().includes(kunci))
        );
        const banding = {
            terbaru: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
            terlama: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
            judul: (a, b) => a.title.localeCompare(b.title, 'id'),
        }[urutan];
        return [...saring].sort(banding);
    }, [semua, kategori, tahunPilih, cari, urutan]);

    useEffect(() => setHalaman(1), [kategori, tahunPilih, cari, urutan]);

    const pilihKategori = (k) => {
        const baru = new URLSearchParams(params);
        if (k) baru.set('kategori', k);
        else baru.delete('kategori');
        setParams(baru, { replace: true });
    };

    const opsiKategori = [{ nama: null, label: 'Semua Kategori' }, ...TOPIK.map((t) => ({ nama: t.kategori, label: t.kategori }))];
    const tampil = hasil.slice((halaman - 1) * PER_HALAMAN, halaman * PER_HALAMAN);

    return (
        <RootLanding>
            <section className="bg-gradient-to-b from-g-600 to-g-700">
                <div className="mx-auto flex max-w-[1440px] flex-col gap-6 px-4 py-12 sm:px-8 lg:p-16">
                    <div className="flex flex-col gap-4">
                        <h1 className="text-3xl font-bold tracking-[-1px] text-white md:text-[40px] md:leading-[52px]">Dokumen & Peraturan</h1>
                        <span className="h-1 w-12 bg-o-500" aria-hidden="true" />
                        <p className="font-body text-base leading-7 text-white md:text-lg">
                            Dokumen & Peraturan yang dibuat berkaitan dengan pelaporan B3
                        </p>
                    </div>
                    <nav aria-label="Jejak halaman" className="flex items-center gap-3 font-body text-sm">
                        <Link to="/" className="text-pb-jejak hover:text-white hover:underline">
                            Beranda
                        </Link>
                        <span className="text-lg text-pb-jejak" aria-hidden="true">
                            ›
                        </span>
                        <span className="font-medium text-white" aria-current="page">
                            Dokumen & Peraturan
                        </span>
                    </nav>
                </div>
            </section>

            <section className="bg-pb-halaman">
                <div className="mx-auto grid max-w-[1440px] gap-8 px-4 pb-16 pt-12 sm:px-8 lg:grid-cols-[280px_1fr] lg:px-16">
                    <aside className="flex flex-col gap-6">
                        <div className="flex flex-col gap-6 rounded-xl border border-pb-bingkai bg-white p-5">
                            <div className="flex flex-col gap-4">
                                <h2 className="text-lg font-bold text-pb-judul">Kategori</h2>
                                <ul className="flex flex-col gap-2">
                                    {opsiKategori.map((o) => {
                                        const aktif = (kategori ?? null) === o.nama;
                                        const jumlah = o.nama ? semua.filter((d) => punya(d, o.nama)).length : semua.length;
                                        return (
                                            <li key={o.label}>
                                                <button
                                                    type="button"
                                                    aria-pressed={aktif}
                                                    onClick={() => pilihKategori(o.nama)}
                                                    className={`flex h-11 w-full items-center gap-3 rounded-lg px-3 text-left font-body text-sm ${
                                                        aktif ? 'bg-o-500 font-semibold text-white' : 'text-pb-judul hover:bg-pb-kabut'
                                                    }`}
                                                >
                                                    <Ikon nama={aktif ? 'centang' : 'berkas'} className={`size-5 shrink-0 ${aktif ? '' : 'text-ink-500'}`} />
                                                    <span className="flex-1">{o.label}</span>
                                                    <span className={`text-xs ${aktif ? 'text-white' : 'text-ink-500'}`}>
                                                        {String(jumlah).padStart(2, '0')}
                                                    </span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                            <hr className="border-pb-bingkai" />
                            <div className="flex flex-col gap-3">
                                <h2 className="text-base font-semibold text-pb-judul">Tahun</h2>
                                <Pilihan
                                    label="Tahun"
                                    nilai={tahunPilih}
                                    setNilai={setTahunPilih}
                                    opsi={[{ kunci: 'semua', label: 'Semua tahun' }, ...daftarTahun.map((t) => ({ kunci: String(t), label: String(t) }))]}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col gap-4 rounded-xl bg-g-600 p-6">
                            <Ikon nama="pesan" className="size-7 text-white" tebal={1.8} />
                            <h2 className="text-lg font-semibold leading-7 text-white">Tidak menemukan dokumen?</h2>
                            <p className="font-body text-sm leading-[22px] text-pb-jejak">
                                Kami siap membantu Anda menemukan informasi yang dibutuhkan.
                            </p>
                            <a
                                href="mailto:ditpb3@kemenlh.go.id"
                                className="inline-flex h-11 items-center justify-center rounded-[10px] bg-white px-5 font-body text-sm font-semibold text-g-600 hover:bg-pb-kabut"
                            >
                                Hubungi kami
                            </a>
                        </div>
                    </aside>

                    <div className="flex min-w-0 flex-col gap-6">
                        <form
                            className="flex flex-col gap-4 md:flex-row"
                            onSubmit={(e) => {
                                e.preventDefault();
                                setCari(ketikan.trim());
                            }}
                        >
                            <label className="flex h-12 flex-1 items-center gap-3 rounded-lg border border-pb-bingkai bg-white px-4 focus-within:border-pb-aksen">
                                <Ikon nama="cari" className="size-4 shrink-0 text-ink-500" />
                                <span className="sr-only">Cari berdasarkan judul dokumen</span>
                                <input
                                    type="search"
                                    value={ketikan}
                                    onChange={(e) => setKetikan(e.target.value)}
                                    placeholder="Cari berdasarkan judul dokumen"
                                    className="w-full bg-transparent font-body text-sm text-pb-judul outline-none placeholder:text-ink-400"
                                />
                            </label>
                            <div className="flex gap-4">
                                <Pilihan
                                    label="Urutkan"
                                    nilai={urutan}
                                    setNilai={setUrutan}
                                    opsi={URUTAN.map((u) => ({ ...u, label: `Urutkan: ${u.label}` }))}
                                    className="flex-1 md:w-[204px] md:flex-none"
                                />
                                <button type="submit" className="h-12 rounded-md bg-pb-tombol px-[22px] font-body text-sm font-semibold text-white hover:bg-g-700">
                                    Cari
                                </button>
                            </div>
                        </form>

                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <h2 className="text-[22px] font-bold tracking-[-0.4px] text-pb-judul">Hasil Pencarian Dokumen</h2>
                            <p className="font-body text-sm text-ink-500">
                                <span className="font-semibold text-pb-aksen">{hasil.length}</span> dokumen ditemukan
                            </p>
                        </div>

                        {isLoading ? (
                            <div className="flex flex-col gap-4">
                                {[0, 1, 2].map((i) => (
                                    <div key={i} className="h-28 animate-pulse rounded-xl bg-white" />
                                ))}
                            </div>
                        ) : tampil.length === 0 ? (
                            <p className="rounded-xl border border-pb-bingkai bg-white p-10 text-center font-body text-sm text-pb-teks">
                                Tidak ada dokumen yang cocok dengan pilihan Anda.
                            </p>
                        ) : (
                            <div className="flex flex-col gap-4">
                                {tampil.map((d) => (
                                    <BarisDokumen key={d.id} doc={d} />
                                ))}
                            </div>
                        )}
                        <Paginasi halaman={halaman} jumlahHalaman={Math.ceil(hasil.length / PER_HALAMAN)} setHalaman={setHalaman} />
                    </div>
                </div>
            </section>
        </RootLanding>
    );
}
